import { AlertCircleIcon, InfoIcon } from 'lucide-react';
import React from 'react';
import { chain } from 'react-aria';
import { type DialogProps, Heading } from 'react-aria-components';
import { Button } from './buttons/Button';
import { Dialog } from './Dialog';
import { tv, type VariantProps } from 'tailwind-variants';

export const promptStyles = tv({
	slots: {
		title: 'my-0 text-xl font-semibold leading-6',
		icon: 'absolute right-6 top-6 size-6 stroke-2 text-blue-500'
	},
	variants: {
		intent: {
			alert: {
				icon: 'text-red-500'
			},
			confirm: {},
			cancelAndConfirm: {},
			custom: {}
		}
	}
});

export interface PromptProps extends Omit<DialogProps, 'children'> {
	intent?: VariantProps<typeof promptStyles>['intent'];
	title: string;
	text: string;
	actionPending?: boolean;
	actionLabel?: string;
	cancelLabel?: string;
	onCancel?: () => void;
	onAction?: () => void;
	closeOnAction?: boolean;
	buttonSlot?: React.ReactNode;
	children?: React.ReactNode;
}

export function Prompt({
	intent = 'alert',
	title,
	cancelLabel,
	actionLabel = 'Ok',
	onAction,
	onCancel,
	text,
	actionPending,
	closeOnAction = true,
	buttonSlot,
	children,
	...props
}: PromptProps) {
	const { title: titleSlot, icon } = promptStyles({ intent });
	return (
		<Dialog role="alertdialog" {...props}>
			{({ close }) => (
				<>
					<Heading slot="title" className={titleSlot()}>
						{title}
					</Heading>
					<div className={icon()}>{intent === 'alert' ? <AlertCircleIcon aria-hidden /> : <InfoIcon aria-hidden />}</div>
					<p className="mt-3 text-slate-500">{text}</p>
					{children}
					<div className="mt-6 flex justify-end gap-2">
						{intent === 'alert' ? (
							<>
								<Button
									appearance="outline"
									onClick={closeOnAction ? chain(onCancel, close) : onCancel}
									label={cancelLabel || 'Cancel'}
								/>
								<Button
									intent="destructive"
									autoFocus
									onClick={closeOnAction ? chain(onAction, close) : onAction}
									label={actionLabel}
									isPending={actionPending}
								/>
							</>
						) : intent === 'cancelAndConfirm' ? (
							<>
								<Button
									intent="destructive"
									onClick={closeOnAction ? chain(onCancel, close) : onCancel}
									label={cancelLabel || 'Cancel'}
								/>
								<Button autoFocus onClick={onAction} label={actionLabel} isPending={actionPending} />
							</>
						) : intent === 'confirm' ? (
							<Button
								autoFocus
								onClick={closeOnAction ? chain(onAction, close) : onAction}
								label={actionLabel}
								isPending={actionPending}
							/>
						) : (
							buttonSlot
						)}
					</div>
				</>
			)}
		</Dialog>
	);
}
